import { ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, signal, ViewChild, WritableSignal, Signal, computed, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { FieldType, FieldTypeConfig } from '@ngx-formly/core'
import { ImageCroppedEvent, ImageCropperModule, LoadedImage } from 'ngx-image-cropper'
import { ApplicationService } from 'src/app/services/application.service'
import { generateToken } from 'src/app/services/helper.service'
import { OrderService } from 'src/app/services/order.service'
import { ProfileService } from 'src/app/services/profile.service'
import { ScannerService } from 'src/app/services/scanner.service'
import { photoHardFails, photoUploadErrorMessage } from 'src/app/services/photo-checks'
import { UserService } from 'src/app/services/user.service'
import { environment } from 'src/environments/environment'
import { LicensePrefill, PhotoChecks, PhotoUploadResponse, QRData, Verification } from 'src/types/scanner'
import { User } from 'src/types/user'
import { Scan } from 'src/types/wizard'
import * as _ from 'lodash'
import { MatButtonModule } from '@angular/material/button'
import { MatInputModule } from '@angular/material/input'
import { QRCodeComponent } from 'angularx-qrcode'
import { PdfViewerModule } from 'ng2-pdf-viewer'
import { ButtonComponent } from 'src/app/components/button/button.component'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask'
import { Upsells } from 'src/types/traveler'
import {
	applyLicensePrefill, applyLicenseValues, compareWithLicense, describePrefill, LicenseMismatch,
	hasPrefillMarker, markPrefillApplied, prefillAlreadyApplied, scannedSinceFix,
} from 'src/app/services/license-prefill.service'

/** ISO/IEC 7810 ID-1 (driver's licence) aspect: 85.60 x 53.98 mm. */
export const LICENSE_ASPECT_RATIO = 85.60 / 53.98
export const LICENSE_TYPES = ['drivers_license_front', 'drivers_license_back']

/** A passport-type photo the analyzer has checked, awaiting the customer's "use" or "choose another". */
interface PhotoReview extends PhotoChecks {
	scan: Scan
	document_uuid: string
	/** A check the photo cannot fail and still be used (PHOTO_HARD_FAIL_TESTS) failed. */
	blocked: boolean
}

@Component({
	selector: 'gwc-secure-scan',
	templateUrl: './secure.scan.component.html',
	styleUrls: ['./secure.scan.component.scss'],
	imports: [
		ButtonComponent,
		ReactiveFormsModule,
		MatButtonModule,
		MatInputModule,
		QRCodeComponent,
		ImageCropperModule,
		PdfViewerModule,
		MatProgressSpinnerModule,
		NgxMaskDirective,
		NgxMaskPipe
	],
	providers: [provideNgxMask()]
})

export class SecureScanComponent extends FieldType<FieldTypeConfig> implements OnInit {
	@ViewChild('input') file_input!: ElementRef

	public sendingText: WritableSignal<boolean> = signal(false)
	public uploadingImage: WritableSignal<boolean> = signal(false)
	public uploadError: WritableSignal<string> = signal('')
	public step: WritableSignal<string> = signal('')
	public is_photo: WritableSignal<boolean> = signal(false)
	public is_text: WritableSignal<boolean> = signal(false)
	public phone = new FormControl('', Validators.required)
	public documents: WritableSignal<Scan[]> = signal([])
	public scans: WritableSignal<Scan[]> = signal([])
	public listening: WritableSignal<string> = signal('')
	public completed: WritableSignal<string[]> = signal([])
	public uploading_file_index: WritableSignal<number> = signal(0)
	public croppedImage: Blob | null = null

	// Driver's licence verdicts by document uuid (checklist shown under each
	// scan), and what the barcode did to the wizard model.
	public verifications: WritableSignal<{ [document_uuid: string]: Verification }> = signal({})
	public prefill: WritableSignal<LicensePrefill | null> = signal(null)
	public prefilled: WritableSignal<string[]> = signal([])
	public mismatches: WritableSignal<LicenseMismatch[]> = signal([])
	public licenseValuesApplied: WritableSignal<string[]> = signal([])
	public prefilledDescription = computed(() => describePrefill(this.prefilled()))
	public licenseValuesDescription = computed(() => describePrefill(this.licenseValuesApplied()))
	private prefillRequested: boolean = false

	// Passport-type photos uploaded here: the analyzer's verdict per document
	// uuid, and the one just uploaded that the customer has not accepted yet.
	public photoChecks: WritableSignal<{ [document_uuid: string]: PhotoChecks }> = signal({})
	public photoReview: WritableSignal<PhotoReview | null> = signal(null)
	// Shown on the crop step when the analyzer could not check a photo and
	// the customer is cropping it by hand instead.
	public photoNotice: WritableSignal<string> = signal('')

	public uploadingDocument = computed(() => this.scans()[this.uploading_file_index()])
	public cropAspectRatio = computed<number | null>(() => {
		const doc = this.uploadingDocument()
		if (doc && LICENSE_TYPES.includes(doc.type)) {
			return LICENSE_ASPECT_RATIO
		}
		return this.props['aspect_ratio'] || null
	})

	public isCompleted = (document_uuid: string): Signal<boolean> => {
		return computed(() => this.completed().includes(document_uuid))
	}
	public qrLink: WritableSignal<string> = signal('')
	public firstScanIndex: number = 0
	private documentsSubscription!: NodeJS.Timeout
	// Every phone-status poll this instance started: all cleared when the
	// customer leaves the step, so a poll that completes after they have
	// gone back to an earlier step cannot write into the model they are
	// editing (the component is rebuilt on each visit; a leaked poll kept
	// filling the shared model from the previous visit).
	private polls: Set<NodeJS.Timeout> = new Set()
	private destroyRef = inject(DestroyRef)
	// Set on destroy so a QR registration that answers late does not start
	// a poll after the teardown has run (it would never be cleared).
	private destroyed: boolean = false
	public imageChangedEvent: WritableSignal<any | null> = signal(null)

	constructor(
		private scannerService: ScannerService,
		private userService: UserService,
		private orderService: OrderService,
		private ref: ChangeDetectorRef,
		private profileService: ProfileService,
		private applicationService: ApplicationService
	) {
		super()
	}

	ngOnInit(): void {
		this.prefillPhone()
		this.getScans()
	}

	ngOnDestroy(): void {
		this.destroyed = true
		this.polls.forEach(poll => clearInterval(poll))
		this.polls.clear()
		if (this.documentsSubscription) {
			clearInterval(this.documentsSubscription)
		}
	}

	public isLicense(document: Scan | undefined | null): boolean {
		return !!document && LICENSE_TYPES.includes(document.type)
	}

	public verificationFor(document_uuid: string): Verification | null {
		return this.verifications()[document_uuid] ?? null
	}

	private prefillPhone(): void {
		this.userService.getUser()
			.subscribe((response: User) => {
				let phone = response.phones.filter(phone => phone.type === 'mobile')[0]

				if (phone && phone.value) {
					this.phone.patchValue(phone.value)
					this.phone.markAsTouched()
					this.phone.markAsDirty()
				}
			})
	}

	private getScans() {
		const docs = this.field.fieldGroup
		const value: any = this.formControl.value
		let index = 0
		let document_list: Scan[] = []
		let request_list: Scan[] = []
		let all_uploads: boolean = true
		let completed_list: string[] = []
		if (docs) {
			docs?.forEach(doc => {
				if (doc.key) {
					let key = doc.key.toString()

					if (key === 'online_passport_photo' || doc.props?.['type'] === 'online_passport_photo') {
						this.is_photo.set(true)
					}

					let document: Scan = {
						key: key,
						document_uuid: '',
						friendly_name: doc.props?.['friendly_name'],
						type: doc.props?.['type'] || key,
						message: doc.props?.['message'],
						key_type: doc.props?.['model_type'] || 'profile'

					}

					let document_value = _.get(value, key)

					if (doc.props?.['model_type']) {
						document.key = doc.key.toString()
						document.key_type = doc.props?.['model_type']
					}

					if (doc.props?.['client_view']) {
						document.client_view = doc.props?.['client_view']
					}

					if (doc.props?.['upsell']) {
						const upsell_type: string = doc.props?.['upsell_type']
						if (upsell_type) {
							const upsell = this.applicationService.upsells()[upsell_type as keyof Upsells]

							if (upsell) {
								document.product_uuid = upsell.uuid
							}
						}
					}

					if (!document_value) {
						request_list.push(document)

						if (document.type !== 'pdf_document') {
							all_uploads = false
						}
					} else {
						document.document_uuid = document_value

						document_list.push(document)

						completed_list.push(document_value)
						index += 1
					}
				}
			})


			if (request_list.length > 0 && !all_uploads) {
				this.generateQRLink(request_list)
			}

			this.documents.set(document_list)
			this.scans.set(request_list)
			this.completed.set(completed_list)
			this.firstScanIndex = index
			this.step.set(request_list.length === 0 ? 'completed' : all_uploads ? 'crop' : 'upload')

			// Returning to a finished step: show the licence checklists again.
			document_list.filter(doc => this.isLicense(doc)).forEach(doc => this.loadVerification(doc.document_uuid))
			if (request_list.length === 0) {
				this.onDocumentsCompleted()
			}
		}
	}

	private generateQRLink(documents: Scan[]) {
		const token: string = generateToken()
		const request_object: QRData = {
			token,
			domain: 'govworks',
			application_uuid: this.applicationService.active_app_uuid(),
			documents
		}

		this.scannerService.saveQRData(request_object)
			.subscribe({
				next: response => {
					if (this.destroyed) {
						return
					}
					// A retake registers a new QR session; the previous poll
					// must not keep running beside the new one.
					if (this.documentsSubscription) {
						clearInterval(this.documentsSubscription)
					}
					this.documentsSubscription = setInterval(() => {
						this.scannerService.pollDemoDocuments(token)
							.subscribe({
								next: response => {
									if (response) {
										if (this.documentsSubscription) {
											clearInterval(this.documentsSubscription)
										}

										if (response.documents) {
											this.documents.set([...this.documents(), ...response.documents])
											this.step.set('phone')
											this.subscribeForDocumentUpdates(this.firstScanIndex)
										}
									}
								},
								error: error => {
								}
							})
					}, 5000)

					this.qrLink.set(`${environment.API}scanner/client/start/qr?token=${token}`)
				}
			})

	}

	public sendText() {
		if (this.scans().length > 0) {
			this.sendingText.set(true)
			this.scannerService.sendText(
				this.phone.value as string,
				this.scans(),
				this.model.profile_uuid,
				this.applicationService.active_traveler_uuid(),
				this.applicationService.active_app_uuid())
				.subscribe({
					next: response => {
						this.is_text.set(true)
						this.documents.set([...this.documents(), ...response.documents])
						this.step.set('phone')
						this.subscribeForDocumentUpdates(this.firstScanIndex)
						this.sendingText.set(false)
						this.ref.detectChanges()
					},
					error: error => {
						this.sendingText.set(false)
					}
				})
		}
	}

	private subscribeForDocumentUpdates(index: number) {
		if (this.destroyed) {
			return
		}
		const current_document = this.documents()[index]
		this.listening.set(current_document.document_uuid)

		let subscription = setInterval(() => {

			this.scannerService.getDocumentStatus(this.listening())
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(response => {
					if (response.status === 'confirmed') {
						this.setDocumentValue(current_document, response.uuid)
						if (this.isLicense(current_document)) {
							this.loadVerification(response.uuid)
						}

						if (subscription) {
							clearInterval(subscription)
						}

						if ((index + 1) < this.documents().length) {
							this.subscribeForDocumentUpdates(index + 1)
						} else {
							this.listening.set('')
							this.step.set('completed')
							this.onDocumentsCompleted(true)
						}

						this.ref.detectChanges()
					}
				})
		}, 3000)
		this.polls.add(subscription)
	}

	/**
	 * `app_uuid` is captured by the caller at request time: a response that
	 * lands after the customer has moved to another application must not
	 * write this model into that application's data model.
	 */
	private saveModel(app_uuid: string = this.applicationService.active_app_uuid()): void {
		// The application uuid lives on the STEPPER's props, not on this
		// field's (buildField() doesn't pass it down). With it undefined the
		// server skips the application sub-model and folds it into the
		// profile blob instead.
		this.profileService.saveProfileWithApplication(app_uuid, this.model)
			.subscribe()
	}

	/** Verdict + checklist for a licence scanned on the phone (no PII). */
	private loadVerification(document_uuid: string): void {
		this.scannerService.getDocumentAnalysis(document_uuid)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: verification => {
					if (verification) {
						this.verifications.set({ ...this.verifications(), [document_uuid]: verification })
					}
				},
				error: () => {
					// The checklist is a nicety; the scan itself is already confirmed.
				}
			})
	}

	/**
	 * Every requested document is in. For a licence, read what the barcode
	 * said: fill in what the customer left blank and list what they typed
	 * differently, so a typo in the licence number or expiration date is
	 * caught here instead of at the AAA desk. Nothing typed is overwritten
	 * unless the customer asks, and a scan fills the model only ONCE: this
	 * runs every time the step is shown (the component is rebuilt on each
	 * visit), and filling blanks again on a revisit undid corrections — a
	 * customer who blanked a middle name the barcode had split off his
	 * first name got it written back each time he came through (prod,
	 * 2026-10). So the model remembers which scan it was filled from, and
	 * blanks are filled only when the model has never been filled before
	 * AND the scan provably has not been applied by the old code: either it
	 * completed while the customer was on this step (`fresh`), or it was
	 * analysed after this fix went live (scannedSinceFix). So:
	 *
	 *   - a revisit never fills from a scan the old code may have applied
	 *     (no marker, analysed before the fix): its blanks would otherwise
	 *     be filled one more time;
	 *   - a revisit DOES fill from a never-applied scan: the first visit's
	 *     request can be cancelled by a quick Continue, fail, or never run
	 *     because the phone finished while the desktop was not watching
	 *     (the server writes the document uuid, so the step loads already
	 *     complete) — without this the prefill would be lost for good;
	 *   - a retaken back (new document uuid) never fills once a marker is
	 *     there: by then everything the customer wants filled is filled,
	 *     and a blank is a choice — the marker is just moved to the new scan.
	 *
	 * The cross-check against what was typed is recomputed on every visit.
	 */
	private onDocumentsCompleted(fresh: boolean = false): void {
		if (this.prefillRequested || !this.documents().some(doc => this.isLicense(doc))) {
			return
		}
		this.prefillRequested = true
		const app_uuid = this.applicationService.active_app_uuid()

		// The server only answers for IDP applications; anything else gets
		// `available: false`, so no licence data ever lands in another
		// product's profile model.
		this.scannerService.getLicensePrefill(app_uuid)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: prefill => {
					// Always replace: a retaken side must not keep showing the
					// previous scan's mismatch or consistency banner.
					this.prefill.set(prefill?.available ? prefill : null)
					this.prefilled.set([])
					this.mismatches.set([])
					this.licenseValuesApplied.set([])
					if (!prefill?.available) {
						this.ref.detectChanges()
						return
					}
					let changed = false
					if (!prefillAlreadyApplied(this.model, prefill)) {
						if (!hasPrefillMarker(this.model) && (fresh || scannedSinceFix(prefill))) {
							const applied = applyLicensePrefill(this.model, prefill)
							this.prefilled.set(applied)
							changed = applied.length > 0
						}
						// Recorded even when nothing was filled: the point is
						// that this scan never writes into the model again.
						changed = markPrefillApplied(this.model, prefill) || changed
					}
					this.mismatches.set(compareWithLicense(this.model, prefill))
					if (changed) {
						this.saveModel(app_uuid)
					}
					// The back's arrival re-verifies the front on the server
					// (front↔back match); pick that up.
					this.documents().filter(doc => this.isLicense(doc)).forEach(doc => this.loadVerification(doc.document_uuid))
					this.ref.detectChanges()
				},
				error: () => {
					this.prefillRequested = false
				}
			})
	}

	/** Customer chose the licence's values over what they typed. */
	public useLicenseValues(): void {
		const prefill = this.prefill()
		if (!prefill) {
			return
		}
		const applied = applyLicenseValues(this.model, prefill, this.mismatches())
		if (applied.length > 0) {
			this.licenseValuesApplied.set(applied)
			this.mismatches.set([])
			this.saveModel()
		}
	}

	public uploadFile(index: number): void {
		if (this.documentsSubscription) {
			clearInterval(this.documentsSubscription)
		}
		this.uploadError.set('')
		this.step.set('crop')
		this.uploading_file_index.set(index)
		setTimeout(() => {
			this.file_input.nativeElement.click()
		}, 300)
	}

	public fileUploaded($event: any) {
		let file_list: FileList

		if ($event.target && $event.target.files) {
			// File input event
			file_list = $event.target.files as FileList

			if (file_list.length > 0) {
				const scan = this.scans()[this.uploading_file_index()]
				const file = file_list[0]

				if (scan?.type === 'online_passport_photo') {
					// Reset the input so the same file can be chosen again
					// after a refusal — only once the File is held: clearing
					// `value` empties the input's FileList in place (Chrome,
					// Firefox). The cropper branch below reads target.files
					// itself, so its event is never cleared.
					$event.target.value = ''
					this.uploadPhoto(file, scan)
					return
				}

				if (this.scans()[this.uploading_file_index()].type === 'pdf_document') {
					$event.target.value = ''
					this.uploadingImage.set(true)
					this.scannerService.uploadFile('direct', file, undefined, this.scans()[this.uploading_file_index()], this.applicationService.active_app_uuid())
						.subscribe({
							next: response => {
								this.uploadingImage.set(false)

								if (response?.document_uuid) {
									const new_document: Scan = {
										...this.scans()[this.uploading_file_index()],
										document_uuid: response.document_uuid
									}

									this.setDocumentValue(new_document, response.document_uuid)

									this.insertDocument(new_document)

									if (this.scans()[this.uploading_file_index() + 1]) {
										this.uploading_file_index.set(this.uploading_file_index() + 1)
										this.imageChangedEvent.set(null)
									} else {
										this.imageChangedEvent.set(null)
										this.step.set('completed')
										this.onDocumentsCompleted(true)
									}
								}
							},
							error: () => this.failUpload()
						})
				} else {
					this.imageChangedEvent.set($event)
				}
			}
		}
	}

	/**
	 * Add a document in the order its field is declared in the step. A
	 * retake removes the old document (clearDocumentValue) rather than
	 * replacing it, so a plain append would drop the retaken document to the
	 * bottom of the list while its siblings stay put.
	 */
	private insertDocument(document: Scan): void {
		const order = this.field.fieldGroup?.map(doc => doc.key?.toString()) ?? []
		const position = order.indexOf(document.key)
		const documents = [...this.documents()]
		const after = position === -1 ? -1 : documents.findIndex(doc => order.indexOf(doc.key) > position)
		documents.splice(after === -1 ? documents.length : after, 0, document)
		this.documents.set(documents)
	}

	private failUpload(message?: string): void {
		this.uploadingImage.set(false)
		this.uploadError.set(message || 'We couldn\'t upload that file. Please check your connection and try again.')
	}

	/**
	 * A passport-type photo goes up as chosen — no cropper — through the
	 * same analyzer run the phone's camera capture gets (framing, compliance
	 * checks, background replacement). The analyzer frames the face itself,
	 * and a hand crop tight around the head is exactly what it refuses.
	 *
	 * The upload is slow (the analyzer's budget is 45 s), so it is tied to
	 * the component's lifetime: a customer who leaves the step mid-upload
	 * finds it incomplete on return and uploads again, rather than having a
	 * late answer written into whatever model they are editing by then.
	 */
	private uploadPhoto(file: File | undefined, scan: Scan): void {
		if (!file) {
			this.failUpload()
			return
		}
		this.uploadingImage.set(true)
		this.uploadError.set('')
		this.photoNotice.set('')
		this.photoReview.set(null)
		this.scannerService.uploadFile('direct', file, 'ppctensorflow', scan, this.applicationService.active_app_uuid())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response: PhotoUploadResponse | null) => {
					this.uploadingImage.set(false)
					if (!response) {
						this.failUpload()
						return
					}
					if (response.checks_unavailable) {
						// Our outage, not their photo: nothing was stored. Fall
						// back to what the desktop did before the analyzer ran
						// on photos — they crop it square and it is saved as-is.
						this.photoNotice.set('We couldn\'t check your photo right now. Crop it to a square below and confirm, and we\'ll save it as it is.')
						this.imageChangedEvent.set({ target: { files: [file] } })
						return
					}
					if (response.error || !response.document_uuid) {
						// A verdict about the photo: nothing was stored, they
						// pick another file.
						this.uploadError.set(photoUploadErrorMessage(response))
						return
					}
					const fails = response.fails ?? []
					this.photoReview.set({
						scan,
						document_uuid: response.document_uuid,
						success: response.success ?? [],
						fails,
						background_removed: response.background_removed === true,
						blocked: photoHardFails(fails).length > 0,
					})
					this.step.set('photo-review')
				},
				error: () => this.failUpload()
			})
	}

	/** "Use this photo" on the review step (hidden when a hard check failed). */
	public usePhoto(): void {
		const review = this.photoReview()
		if (!review || review.blocked) {
			return
		}
		this.acceptPhoto(review.scan, review.document_uuid, { success: review.success, fails: review.fails, background_removed: review.background_removed })
	}

	/** "Choose a different photo": back to the file picker; the refused upload stays unreferenced. */
	public chooseAnotherPhoto(): void {
		this.photoReview.set(null)
		this.uploadError.set('')
		this.photoNotice.set('')
		this.imageChangedEvent.set(null)
		this.step.set('crop')
		setTimeout(() => this.file_input?.nativeElement.click(), 300)
	}

	private acceptPhoto(scan: Scan, document_uuid: string, checks: PhotoChecks): void {
		const new_document: Scan = { ...scan, document_uuid }
		// The phone's "Use" marks the document confirmed, and the manifests'
		// photo card only reads confirmed photos. Best effort: the uuid is
		// already in the model either way.
		this.scannerService.confirmDocument(document_uuid)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({ error: () => {} })
		this.photoChecks.set({ ...this.photoChecks(), [document_uuid]: checks })
		this.photoReview.set(null)
		this.setDocumentValue(new_document, document_uuid)
		this.insertDocument(new_document)

		const index = this.uploading_file_index()
		this.imageChangedEvent.set(null)
		if (this.scans()[index + 1]) {
			this.uploading_file_index.set(index + 1)
			this.step.set('crop')
		} else {
			this.step.set('completed')
			this.onDocumentsCompleted(true)
		}
	}

	/**
	 * Forget the document this side currently points at — form control,
	 * model, completed list, verification, documents() — so the step is
	 * incomplete until a new upload succeeds. Without this a rejected retake
	 * left the PREVIOUS uuid in the model and the wizard carried on with it
	 * (prod, 2026-09-14: an expired licence was refused twice and the
	 * application was submitted seconds later on the earlier back).
	 *
	 * The key is REMOVED, never set to null: the server's progress counts a
	 * key that exists (Arr::has), so null would mark the side complete with
	 * no document at all. Saves are full-model replaces, so an absent key
	 * clears it server-side on the next save. `persist` sends that save now
	 * (hard stop: the old document must not survive the customer leaving);
	 * a Retake only clears in memory and lets the stepper's own save carry
	 * it, so abandoning the retake keeps what was there.
	 */
	private clearDocumentValue(document: Scan, persist: boolean): void {
		const previous = _.get(this.model, document.key)
		this.formControl.get(document.key)?.reset()
		_.unset(this.model, document.key)
		// By key, never by type: pdf_document (and others) is shared by several
		// keys, and a retake of one must not drop its siblings.
		this.documents.set(this.documents().filter(doc => doc.key !== document.key))
		if (!previous) {
			return
		}
		this.completed.set(this.completed().filter(uuid => uuid !== previous))
		const verifications = { ...this.verifications() }
		delete verifications[previous]
		this.verifications.set(verifications)
		if (persist) {
			this.saveModel()
		}
	}

	private setDocumentValue(document: Scan, value_uuid: string): void {
		const pathSegments = document.key.split('.')
		let control = this.formControl as any as FormGroup

		for (const segment of pathSegments) {
			control = control.controls[segment] as FormGroup
		}

		(control as unknown as FormControl).patchValue(value_uuid)
		_.set(this.model, document.key, value_uuid)

		this.completed.set([...this.completed(), value_uuid])

		// Save the model when a photo is confirmed. If there are multiple photos, the model is saved after each photo is individually confirmed
		this.saveModel()
	}

	public onDragOver(event: DragEvent): void {
		event.preventDefault()
		event.stopPropagation()
		if (event.target instanceof HTMLElement &&
			(event.target.classList.contains('gwc-secure-scan__upload-area') ||
				event.target.classList.contains('gwc-secure-scan__crop-upload-area'))) {
			event.target.style.border = '1px dashed #0087DD'
			event.target.style.opacity = '0.7'
		}
	}

	public onDragLeave(event: DragEvent): void {
		event.preventDefault()
		event.stopPropagation()
		if (event.target instanceof HTMLElement &&
			(event.target.classList.contains('gwc-secure-scan__upload-area') ||
				event.target.classList.contains('gwc-secure-scan__crop-upload-area'))) {
			event.target.style.opacity = '1'
			event.target.style.border = '1px solid #0087DD'
		}
	}

	public onDrop(event: DragEvent): void {
		event.preventDefault()
		event.stopPropagation()

		const files = event.dataTransfer?.files
		if (files && files.length > 0) {
			if (this.documentsSubscription) {
				clearInterval(this.documentsSubscription)
			}
			this.step.set('crop')

			const currentScans = this.scans()
			if (currentScans.length === 1) {
				// Retake scenario - only one document
				this.uploading_file_index.set(0)
			}

			const syntheticEvent = {
				files: files
			}
			this.fileUploaded(syntheticEvent)
		}
	}

	public retakePhoto(document: Scan, index: number) {
		// Documents that came back from the SMS/QR start don't carry the
		// request message, and the direct-upload endpoint requires it.
		const request_list = [{ ...document, document_uuid: '', message: document.message || this.messageFor(document.key ?? document.type) }]
		// Resetting the control is not enough — the model kept the old uuid
		// and the stepper saved it back. Forget the side (in memory; the
		// stepper's save carries it, so abandoning keeps the old document).
		this.clearDocumentValue(document, false)
		const new_documents: Scan[] = this.documents()
		this.scans.set(request_list)
		const verifications = { ...this.verifications() }
		delete verifications[document.document_uuid]
		this.verifications.set(verifications)
		// Everything derived from the previous pair of scans is stale now.
		this.prefillRequested = false
		this.prefill.set(null)
		this.prefilled.set([])
		this.mismatches.set([])
		this.licenseValuesApplied.set([])
		this.firstScanIndex = new_documents.length

		// A PDF can't be taken with the phone's camera, so the first pass
		// sends these straight to the file picker (getScans' all_uploads). A
		// retake has to do the same: offering the QR code here hands the
		// customer a scanner the document can't come from.
		if (request_list.every(doc => doc.type === 'pdf_document')) {
			if (this.documentsSubscription) {
				clearInterval(this.documentsSubscription)
			}
			this.uploadError.set('')
			this.imageChangedEvent.set(null)
			this.uploading_file_index.set(0)
			this.step.set('crop')
			return
		}

		this.generateQRLink(request_list)
		this.step.set('upload')
	}

	private messageFor(key: string | undefined | null): string {
		const wanted = String(key ?? '')
		const doc = this.field.fieldGroup?.find(doc => doc.key?.toString() === wanted)
		return doc?.props?.['message'] || `Take a picture of your ${wanted.replace(/_/g, ' ') || 'document'}.`
	}

	public getDocumentImg(document_uuid: string): string {
		return `${environment.API}scanner/document/${document_uuid}/data`
	}

	public fileChangeEvent(event: any): void {
		this.imageChangedEvent = event;
	}

	public imageCropped(event: ImageCroppedEvent) {
		if (event.blob) {
			this.croppedImage = event.blob
		}
	}

	public uploadCroppedImage() {
		const index = this.uploading_file_index()
		const scans = this.scans()
		if (this.croppedImage && index !== null) {
			this.uploadingImage.set(true)
			this.uploadError.set('')
			this.scannerService.uploadFile('direct', this.croppedImage, undefined, scans[index], this.applicationService.active_app_uuid())
				.subscribe({
					next: response => {
						this.uploadingImage.set(false)

						if (response?.document_uuid) {
							const new_document: Scan = {
								...scans[index],
								document_uuid: response.document_uuid
							}
							if (response.verification) {
								this.verifications.set({ ...this.verifications(), [response.document_uuid]: response.verification })
							}

							const pathSegments = new_document.key.split('.')
							let control = this.formControl as any as FormGroup

							for (const segment of pathSegments) {
								control = control.controls[segment] as FormGroup
							}
							control.patchValue(response.document_uuid)
							_.set(this.model, new_document.key, response.document_uuid)

							this.completed.set([...this.completed(), response.document_uuid])

							const currentDocuments = this.documents()
							if (scans.length === 1) {
								const originalIndex = currentDocuments.findIndex(doc => doc.type === new_document.type)
								if (originalIndex !== -1) {
									const updatedDocuments = [...currentDocuments]
									updatedDocuments[originalIndex] = new_document
									this.documents.set(updatedDocuments)
								} else {
									this.documents.set([...currentDocuments, new_document])
								}
							} else {
								this.documents.set([...currentDocuments, new_document])
							}

							const finalDocuments = this.documents()
							const sortedDocuments = finalDocuments.sort((a, b) => {
								// Front first, back second
								const order = { 'drivers_license_front': 0, 'drivers_license_back': 1 }
								const aOrder = order[a.type as keyof typeof order] ?? 999
								const bOrder = order[b.type as keyof typeof order] ?? 999
								return aOrder - bOrder
							})
							this.documents.set(sortedDocuments)

							if (this.scans()[index + 1]) {
								this.uploading_file_index.set(index + 1)
								this.imageChangedEvent.set(null)
							} else {
								this.imageChangedEvent.set(null)
								this.step.set('completed')
								// The document uuid reaches the server through the
								// stepper's own diff-save on Continue/Finish, as before.
								this.onDocumentsCompleted(true)
							}
						} else {
							// A verdict (expired licence, not a licence...) comes back
							// as {error, message} with nothing stored: say why.
							if (response?.verification?.hard_stop) {
								// The card itself was refused: whatever this side pointed
								// at before must not carry the application forward.
								this.clearDocumentValue(scans[index], true)
							}
							this.failUpload(response?.error ? response.message : undefined)
						}
					},
					error: () => this.failUpload()
				})
		}
	}

	imageLoaded(image: LoadedImage) {
		// show cropper
	}

	cropperReady() {
		// cropper ready
	}

	loadImageFailed() {
		// Back to the file button so another file can be chosen; clear the
		// input's value or picking the same file again won't fire `change`.
		this.imageChangedEvent.set(null)
		this.croppedImage = null
		if (this.file_input?.nativeElement) {
			this.file_input.nativeElement.value = ''
		}
		this.uploadError.set('We couldn\'t open that image. Please choose a JPEG or PNG photo.')
	}
}
